// src/views/bots/components/BotFlowPreview.tsx
// Interactive Bot Flow Preview - Graph-driven WhatsApp chat simulation
// Users can click buttons/options to explore different branch paths

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './BotFlowPreview.module.css';
import logger from '../../../utils/logger';
import { Node, Edge } from '@xyflow/react';

/**
 * Legacy question format (used by BotPreviewButton / old form)
 */
interface LegacyQuestion {
  id: string;
  question: string;
  type: string;
  required: boolean;
  interactive?: any;
  templateData?: any;
}

/**
 * Props - supports both flow builder (nodes+edges) and old form (questions[])
 */
interface BotFlowPreviewProps {
  botName: string;
  keywords: string[];
  // Flow builder mode (graph-driven interactive preview)
  nodes?: Node[];
  edges?: Edge[];
  // Legacy form mode (linear auto-play preview)
  questions?: LegacyQuestion[];
  selectedTemplate?: any;
  responseType?: 'template' | 'questionnaire';
  onClose?: () => void;
}

/**
 * Convert flat questions array into simple linear nodes + edges
 * so the same graph-driven preview engine can handle both modes
 */
function questionsToGraph(questions: LegacyQuestion[]): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [
    {
      id: 'start',
      type: 'startNode',
      position: { x: 0, y: 0 },
      data: { keywords: [] },
    },
  ];
  const edges: Edge[] = [];

  questions.forEach((q, i) => {
    const nodeId = q.id || `legacy-${i}`;
    const isInteractive = q.type === 'interactive-button' || q.type === 'interactive-list';

    nodes.push({
      id: nodeId,
      type: q.type || 'text',
      position: { x: 0, y: (i + 1) * 200 },
      data: {
        questionType: q.type,
        question: q.question,
        required: q.required,
        interactive: q.interactive,
      },
    });

    // Connect to previous node
    const prevId = i === 0 ? 'start' : (questions[i - 1].id || `legacy-${i - 1}`);
    const sourceHandle = i === 0 ? 'start-output' : 'question-output';
    edges.push({
      id: `edge-${prevId}-${nodeId}`,
      source: prevId,
      target: nodeId,
      sourceHandle,
    } as Edge);
  });

  return { nodes, edges };
}

/**
 * A single message in the chat conversation
 */
interface ChatMessage {
  id: string;
  type: 'user' | 'bot' | 'system';
  content: string;
  timestamp: string;
  interactive?: any;
  nodeId?: string; // Which flow node this message came from
}

/**
 * Format time for chat display
 */
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Generate a sample user response based on question type
 */
const getSampleResponse = (type: string): string => {
  const responses: Record<string, string> = {
    text: 'John Doe',
    email: 'john@example.com',
    phone: '+1 555-123-4567',
    number: '25',
    location: 'New York, NY',
  };
  return responses[type] || 'Sample response';
};

/**
 * Interactive Bot Flow Preview Component
 * Traverses the flow graph and lets users explore conditional branches
 */
const BotFlowPreview: React.FC<BotFlowPreviewProps> = ({
  botName,
  keywords,
  nodes: nodesProp,
  edges: edgesProp,
  questions: questionsProp,
  onClose,
}) => {
  // Resolve nodes/edges: use direct props if available, otherwise convert from questions
  const { nodes, edges } = React.useMemo(() => {
    if (nodesProp && edgesProp && nodesProp.length > 0) {
      return { nodes: nodesProp, edges: edgesProp };
    }
    if (questionsProp && questionsProp.length > 0) {
      return questionsToGraph(questionsProp);
    }
    return { nodes: [] as Node[], edges: [] as Edge[] };
  }, [nodesProp, edgesProp, questionsProp]);

  logger.log('[BotFlowPreview] Initialized', {
    botName,
    nodeCount: nodes.length,
    edgeCount: edges.length,
    mode: nodesProp ? 'flow-builder' : 'legacy-questions',
  });

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [waitingForChoice, setWaitingForChoice] = useState(false);
  const [pathHistory, setPathHistory] = useState<string[]>([]); // Track chosen path for breadcrumb
  const [isFlowComplete, setIsFlowComplete] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const messageCountRef = useRef(0);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Safe setTimeout that gets cleaned up on unmount
  const safeTimeout = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timeoutIdsRef.current.push(id);
    return id;
  }, []);

  // Cleanup all pending timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach(clearTimeout);
      timeoutIdsRef.current = [];
    };
  }, []);

  /**
   * Auto-scroll to bottom when messages change
   */
  useEffect(() => {
    if (chatRef.current) {
      setTimeout(() => {
        chatRef.current?.scrollTo({
          top: chatRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [messages, isTyping]);

  /**
   * Add a message to the conversation
   */
  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    messageCountRef.current += 1;
    const newMsg: ChatMessage = {
      ...msg,
      id: `msg-${messageCountRef.current}`,
      timestamp: formatTime(new Date()),
    };
    setMessages((prev) => [...prev, newMsg]);
    return newMsg;
  }, []);

  /**
   * Find the next node following a specific edge from a source node
   */
  const findNextNode = useCallback(
    (fromNodeId: string, sourceHandle?: string): Node | null => {
      // Find matching outgoing edge
      const edge = edges.find((e) => {
        if (e.source !== fromNodeId) return false;
        if (sourceHandle) return e.sourceHandle === sourceHandle;
        // Default: follow question-output handle
        return e.sourceHandle === 'question-output' || e.sourceHandle === 'start-output';
      });

      if (!edge) return null;
      return nodes.find((n) => n.id === edge.target) || null;
    },
    [nodes, edges]
  );

  /**
   * Process a flow node - show bot message and handle next step
   */
  const processNode = useCallback(
    (node: Node) => {
      if (!node || node.type === 'startNode') return;

      const questionType = (node.data.questionType as string) || node.type || 'text';
      const isInteractive = questionType === 'interactive-button' || questionType === 'interactive-list';

      // Get the question text
      let questionText = '';
      if (isInteractive) {
        questionText = (node.data.interactive as any)?.body?.text || (node.data.question as string) || '';
      } else {
        questionText = (node.data.question as string) || '';
      }

      // Show typing indicator briefly, then show bot message
      setIsTyping(true);
      safeTimeout(() => {
        setIsTyping(false);

        // Add bot message
        addMessage({
          type: 'bot',
          content: questionText,
          interactive: isInteractive ? node.data.interactive : undefined,
          nodeId: node.id,
        });

        setCurrentNodeId(node.id);

        if (isInteractive) {
          // Wait for user to click a button/option
          setWaitingForChoice(true);
        } else {
          // Non-interactive: auto-respond after a delay
          safeTimeout(() => {
            const response = getSampleResponse(questionType);
            addMessage({ type: 'user', content: response });

            // Follow to next node
            const nextNode = findNextNode(node.id, 'question-output');
            if (nextNode) {
              safeTimeout(() => processNode(nextNode), 800);
            } else {
              // No next node - flow is complete
              setIsFlowComplete(true);
              addMessage({
                type: 'system',
                content: 'End of flow — no more steps connected',
              });
            }
          }, 1200);
        }
      }, 800);
    },
    [addMessage, findNextNode, safeTimeout]
  );

  /**
   * Handle user clicking a button/list option in the preview
   */
  const handleOptionClick = useCallback(
    (optionId: string, optionTitle: string) => {
      if (!currentNodeId || !waitingForChoice) return;

      setWaitingForChoice(false);

      // Add user's choice as a message
      addMessage({ type: 'user', content: optionTitle });

      // Track path
      setPathHistory((prev) => [...prev, optionTitle]);

      // Find the branch edge matching this option
      const branchHandle = `branch-${optionId}`;
      let nextNode = findNextNode(currentNodeId, branchHandle);

      // Fallback: if no branch-specific edge, try the sequential question-output edge
      // This handles non-branching interactive nodes where all options go to the same next step
      if (!nextNode) {
        nextNode = findNextNode(currentNodeId, 'question-output');
      }

      if (nextNode) {
        const resolvedNode = nextNode;
        safeTimeout(() => processNode(resolvedNode), 800);
      } else {
        // No target for this branch - flow ends here
        setIsFlowComplete(true);
        addMessage({
          type: 'system',
          content: `No step connected for "${optionTitle}" — connect a node to this branch in the builder`,
        });
      }
    },
    [currentNodeId, waitingForChoice, addMessage, findNextNode, processNode, safeTimeout]
  );

  /**
   * Start/restart the preview flow
   */
  const startFlow = useCallback(() => {
    setMessages([]);
    setCurrentNodeId(null);
    setIsTyping(false);
    setWaitingForChoice(false);
    setPathHistory([]);
    setIsFlowComplete(false);
    messageCountRef.current = 0;

    // Clear any pending timeouts from previous flow
    timeoutIdsRef.current.forEach(clearTimeout);
    timeoutIdsRef.current = [];

    // 1. User sends keyword
    const triggerKeyword = keywords[0] || 'hello';
    safeTimeout(() => {
      addMessage({ type: 'user', content: triggerKeyword });

      // 2. Find the first node after START
      const startNode = nodes.find((n) => n.type === 'startNode');
      if (!startNode) {
        addMessage({ type: 'system', content: 'No START node found' });
        return;
      }

      const firstNode = findNextNode(startNode.id, 'start-output');
      if (!firstNode) {
        addMessage({
          type: 'system',
          content: 'No nodes connected to START — add a node and connect it',
        });
        return;
      }

      // 3. Process the first node
      safeTimeout(() => processNode(firstNode), 600);
    }, 400);
  }, [keywords, nodes, addMessage, findNextNode, processNode, safeTimeout]);

  /**
   * Auto-start the flow on mount
   */
  useEffect(() => {
    startFlow();
  }, []); // Only on mount

  /**
   * Render interactive buttons for the LATEST interactive message only
   * (previous interactive messages show as static)
   */
  const renderInteractiveChoices = (msg: ChatMessage, isLatest: boolean) => {
    if (!msg.interactive) return null;
    const canClick = isLatest && waitingForChoice;

    if (msg.interactive.type === 'button') {
      const buttons = msg.interactive.action?.buttons || [];
      return (
        <div className={styles.buttonsList}>
          {buttons.map((button: any, index: number) => (
            <button
              key={index}
              className={`${styles.whatsappButton} ${canClick ? styles.clickable : styles.disabled}`}
              onClick={() => canClick && handleOptionClick(button.reply?.id, button.reply?.title)}
              disabled={!canClick}
              type="button"
            >
              {button.reply?.title || `Button ${index + 1}`}
            </button>
          ))}
        </div>
      );
    }

    if (msg.interactive.type === 'list') {
      const sections = msg.interactive.action?.sections || [];
      const menuButtonText = msg.interactive.action?.button || 'View Options';
      return (
        <div className={styles.listPreview}>
          <div className={`${styles.listMenuTrigger} ${!canClick ? styles.disabled : ''}`}>
            ☰ {menuButtonText}
          </div>
          {canClick && sections.map((section: any, sIndex: number) => (
            <div key={sIndex} className={styles.listSection}>
              <div className={styles.sectionTitle}>{section.title}</div>
              {section.rows?.map((row: any, rIndex: number) => (
                <button
                  key={rIndex}
                  className={`${styles.listRow} ${styles.clickable}`}
                  onClick={() => handleOptionClick(row.id, row.title)}
                  type="button"
                >
                  <span className={styles.listRowTitle}>{row.title}</span>
                  {row.description && (
                    <span className={styles.listRowDesc}>{row.description}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Identify the last interactive bot message for click handling
  const lastInteractiveMsgId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'bot' && messages[i].interactive) return messages[i].id;
    }
    return null;
  })();

  return (
    <div className={styles.previewOverlay} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={styles.previewContainer}>
        {/* Header */}
        <div className={styles.previewHeader}>
          <div className={styles.headerLeft}>
            <h2>Bot Preview</h2>
            <div className={styles.botInfo}>
              <span className={styles.botName}>{botName || 'Your Bot'}</span>
              {pathHistory.length > 0 && (
                <span className={styles.pathBadge}>
                  Path: {pathHistory.join(' → ')}
                </span>
              )}
            </div>
          </div>
          <div className={styles.headerControls}>
            <button
              className={styles.restartBtn}
              onClick={startFlow}
              type="button"
              title="Restart the preview from the beginning"
            >
              Restart
            </button>
            {onClose && (
              <button className={styles.closeBtn} onClick={onClose} type="button">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* WhatsApp Chat View */}
        <div className={styles.previewContent}>
          <div className={styles.chatView}>
            <div className={styles.whatsappContainer}>
              {/* WhatsApp Header */}
              <div className={styles.whatsappHeader}>
                <div className={styles.contactInfo}>
                  <div className={styles.avatar}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M20 21a8 8 0 1 0-16 0" />
                    </svg>
                  </div>
                  <div className={styles.contactDetails}>
                    <div className={styles.contactName}>{botName || 'Bot'}</div>
                    <div className={styles.contactStatus}>
                      {isTyping ? 'typing...' : waitingForChoice ? 'waiting for your choice...' : 'online'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className={styles.chatMessages} ref={chatRef}>
                {messages.map((msg) => (
                  <div key={msg.id} className={styles.messageGroup}>
                    {/* System Messages */}
                    {msg.type === 'system' && (
                      <div className={styles.systemMessage}>{msg.content}</div>
                    )}

                    {/* User Messages */}
                    {msg.type === 'user' && (
                      <div className={styles.userMessage}>
                        <div className={styles.messageContent}>{msg.content}</div>
                        <div className={styles.messageTime}>{msg.timestamp} ✓✓</div>
                      </div>
                    )}

                    {/* Bot Messages */}
                    {msg.type === 'bot' && (
                      <div className={styles.botMessage}>
                        <div className={styles.messageContent}>
                          {msg.interactive ? (
                            <div className={styles.whatsappInteractiveButtons}>
                              {msg.interactive.header?.text && (
                                <div className={styles.interactiveHeader}>
                                  {msg.interactive.header.text}
                                </div>
                              )}
                              {msg.content && (
                                <div className={styles.interactiveBody}>{msg.content}</div>
                              )}
                              {msg.interactive.footer?.text && (
                                <div className={styles.interactiveFooter}>{msg.interactive.footer.text}</div>
                              )}
                              {renderInteractiveChoices(msg, msg.id === lastInteractiveMsgId)}
                            </div>
                          ) : (
                            <div className={styles.textMessage}>{msg.content}</div>
                          )}
                        </div>
                        <div className={styles.messageTime}>{msg.timestamp}</div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className={styles.typingIndicator}>
                    <div className={styles.typingDots}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}

                {/* Flow complete message with restart */}
                {isFlowComplete && (
                  <div className={styles.flowCompleteBar}>
                    <span>Flow complete</span>
                    <button onClick={startFlow} type="button">
                      Try a different path
                    </button>
                  </div>
                )}
              </div>

              {/* WhatsApp Input Bar */}
              <div className={styles.whatsappInput}>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    placeholder={
                      waitingForChoice
                        ? 'Choose an option above...'
                        : 'Preview mode — responses are simulated'
                    }
                    disabled
                    className={styles.messageInput}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotFlowPreview;
